import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { normalizeNetworkName } from '~publish/helpers/channels'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { useCommunityConfig } from '../../CommunityConfig'
import {
  useCommentData,
  useCommentReactionError,
} from '../../Comments/context/CommentContext'
import type { ReactionType } from '../../hooks/configuration/useConfigurationQuery'
import {
  CommentDeletedError,
  useReactToComment,
} from '../../hooks/comments/useReactToComment'
import { VIEW_ONLY_COMMENT_TOOLTIP } from '../../shared/viewOnlyCopy'
import { isChannelIconService } from '../../util/isChannelIconService'
import { MultiReactions } from '../APIReaction/MultiReactions'
import { SingleReaction } from '../APIReaction/SingleReaction'

import type { JSX } from 'react'

export const CommentReactions_Channel = graphql(/* GraphQL */ `
  fragment CommentReactions_Channel on Channel @_unmask {
    service
    type
    allowedActions
  }
`)

type CommentReactionsProps = {
  channel: FragmentOf<typeof CommentReactions_Channel>
}

/**
 * CommentReactions - Displays a like/reaction button for a comment.
 * Renders the appropriate reaction component based on channel capabilities:
 * - MultiReactions: For channels with multiple reaction types (e.g. LinkedIn reactions)
 * - SingleReaction: For channels that support a single like via API
 *
 * Channels whose only supported reaction is `external` can't like a comment
 * through their API, so no reaction button is shown at all — the comment can
 * still be opened on the source network from the header's hover actions.
 */
export const CommentReactions = ({
  channel,
}: CommentReactionsProps): JSX.Element | null => {
  const { config } = useCommunityConfig()
  const comment = useCommentData()
  const { reactToComment, loading } = useReactToComment()
  const { setReactionError } = useCommentReactionError()

  /**
   * Applies the picked reaction and owns the comment-side error copy. Rethrows
   * so `MultiReactions` rolls its optimistic state back.
   */
  const handleReact = async (
    reactionType: ReactionType,
    isActive: boolean,
  ): Promise<void> => {
    setReactionError(null)

    try {
      await reactToComment(comment.id, isActive, reactionType)
    } catch (error) {
      // A deleted comment is already marked in the cache, so the card explains
      // itself — an error banner on top of that would only repeat it.
      if (!(error instanceof CommentDeletedError)) {
        setReactionError(
          'An error occurred while reacting to the comment. Please try again later or contact support',
        )
      }

      throw error
    }
  }

  // Don't show reactions for deleted comments
  if (comment.isDeleted) {
    return null
  }

  if (!config.isChannelSupported(channel)) {
    return null
  }

  // View-only actors still see the existing reaction state — Single and
  // Multi render in a disabled state with a tooltip.
  const canManage = canPerform(channel, 'manageComments')

  const likingIcon = config.getLikingIcon(channel)
  const supportsMultiReactions = config.supportsMultiReactions(channel)
  const supportedReactions =
    config.getReactionsConfiguration(channel)?.supportedReactions ?? []
  const targetLabel = `comment from ${comment.author.name || comment.author.username}: ${comment.text}`

  // A comment can be liked via Buffer only when the channel exposes a real
  // reaction. Networks whose sole reaction is `external` (e.g. Instagram,
  // Threads) can't be liked through their API — show no reaction button; the
  // comment is still reachable via the header's "open in a new tab" action.
  const canReactViaBuffer = supportedReactions.some(
    (reaction) => reaction.reactionType !== 'external',
  )

  if (!canReactViaBuffer) {
    return null
  }

  const channelService = isChannelIconService(channel.service)
    ? channel.service
    : null
  if (!channelService) {
    return null
  }

  if (supportsMultiReactions) {
    return (
      <MultiReactions
        likingIcon={likingIcon}
        supportedReactions={supportedReactions}
        isReacted={(comment.reactions?.length ?? 0) > 0}
        currentReactionType={comment.reactions?.[0]?.type}
        loading={loading}
        onReact={handleReact}
        entity="comment"
        service={channelService}
        networkName={normalizeNetworkName(channel.service)}
        targetLabel={targetLabel}
        disabled={!canManage}
        viewOnlyTooltip={VIEW_ONLY_COMMENT_TOOLTIP}
      />
    )
  }

  return (
    <SingleReaction
      likingIcon={likingIcon}
      service={channelService}
      networkName={normalizeNetworkName(channel.service)}
      targetLabel={targetLabel}
      disabled={!canManage}
    />
  )
}
